import React, { useState } from 'react';
import styled from '@emotion/styled';
import { Collapse, InputAdornment, Popover, useTheme } from '@mui/material';
import { CaretDown, CaretRight, MagnifyingGlass, Layout } from '@phosphor-icons/react';
import { Switch } from 'components/inputs/Switch';
import { SortableContainer } from './SortableContainer';
import { CheckboxWrapper } from './CheckboxWrapper';
import { useTableContext } from '../../providers/TableProvider';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  rectIntersection,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

import {
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import type { ColumnSelectorItems } from '../../types';
import type { UniqueIdentifier, DragOverEvent } from '@dnd-kit/core';
import { Trans, t } from '@lingui/macro';
import { Button, IconButton, Text, TextInput } from 'components';

export const ColumnSelector = () => {
  const theme = useTheme();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { table, config: global } = useTableContext();
  const { columnFilter } = global;

  const config = {
    ...columnFilter,
    excluded: [...(columnFilter?.excluded || []), 'Selector', 'Filler'],
    disabled: [...(columnFilter?.disabled || []), ...(global.selectorDisabled || [])],
  };

  const [items, setItems] = React.useState<ColumnSelectorItems>({
    frozenColumns: [],
    defaultColumns: [],
    customFields: [],
  });

  // Helper to check if a column is a custom field (prefixed with 'cf_')
  const isCustomFieldColumn = (columnId: string) => columnId.startsWith('cf_');

  // Get table leaf columns
  const leafColumns = table.getAllLeafColumns();

  // Split up the columns into frozen, default, and custom fields
  const columns = React.useMemo(() => {
    return leafColumns?.reduce(
      (acc, column) => {
        const isExcluded = config.excluded.find(id => id === column.id);
        const isDisabled = Boolean(config.disabled?.find(id => id === column.id));
        column.isDisabled = isDisabled;

        if (isExcluded) return acc;

        // Separate custom fields into their own group
        if (isCustomFieldColumn(column.id)) {
          acc.customFields.push(column);
        } else if (column.getIsPinned()) {
          acc.frozenColumns.push(column);
        } else {
          acc.defaultColumns.push(column);
        }

        return acc;
      },
      { frozenColumns: [], defaultColumns: [], customFields: [] } as ColumnSelectorItems
    );
  }, [leafColumns]);

  // Set columns to state whenever the columns changes
  React.useEffect(() => setItems(columns), [columns]);

  // Check if all general (non-custom field) columns are visible.
  // Derive from columns and depend on columnVisibility so the switch updates when visibility changes.
  const areAllGeneralColumnsVisible = React.useMemo(() => {
    const generalColumns = [...(columns.frozenColumns || []), ...(columns.defaultColumns || [])];
    return generalColumns.every(col => col.getIsVisible());
  }, [columns, table.getState().columnVisibility]);

  // Check if all custom field columns are visible.
  // Derive from columns and depend on columnVisibility so the switch updates when visibility changes.
  const areAllCustomFieldsVisible = React.useMemo(() => {
    const customFields = columns.customFields || [];
    if (customFields.length === 0) return true;
    return customFields.every(col => col.getIsVisible());
  }, [columns, table.getState().columnVisibility]);

  // Toggle visibility for general columns only
  const toggleGeneralColumnsVisibility = () => {
    const newVisibility = !areAllGeneralColumnsVisible;
    const generalColumns = [...(items.frozenColumns || []), ...(items.defaultColumns || [])];

    const columnVisibility: Record<string, boolean> = {};
    generalColumns.forEach(column => {
      const isDisabled = config.disabled?.find(id => id === column.id);
      columnVisibility[column.id] = isDisabled ? true : newVisibility;
    });

    table.setColumnVisibility(prev => ({ ...prev, ...columnVisibility }));
  };

  // Toggle visibility for custom field columns only
  const toggleCustomFieldsVisibility = () => {
    const newVisibility = !areAllCustomFieldsVisible;
    const customFields = items.customFields || [];

    const columnVisibility: Record<string, boolean> = {};
    customFields.forEach(column => {
      const isDisabled = config.disabled?.find(id => id === column.id);
      columnVisibility[column.id] = isDisabled ? true : newVisibility;
    });

    table.setColumnVisibility(prev => ({ ...prev, ...columnVisibility }));
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  function handleDragEnd() {
    // Include custom fields in the column order (after default columns)
    const columnOrder = [
      { id: 'Selector' },
      ...items.frozenColumns,
      ...items.defaultColumns,
      ...(items.customFields || []),
      { id: 'Filler' },
    ].map(item => item.id);

    table.setColumnOrder(columnOrder);
    const primaryPinned = items.frozenColumns
      .filter(col => col.columnDef.meta?.primary)
      .map(col => col.id);
    const otherPinned = items.frozenColumns
      .filter(col => !col.columnDef.meta?.primary)
      .map(col => col.id);
    table.setColumnPinning({ left: ['Selector', ...primaryPinned, ...otherPinned], right: [] });
  }

  const findContainer = (id: UniqueIdentifier) => {
    let containerId = '';

    Object.keys(items).forEach((key: string) => {
      items[key].forEach(item => {
        if (item.id === id) {
          containerId = key;
        }
      });
    });
    return containerId;
  };

  function handleDragOver({ active, over }: DragOverEvent) {
    const overId = over?.id;

    if (!overId) return;

    const overContainer = findContainer(overId) || over.id;
    const activeContainer = findContainer(active.id);

    if (!overContainer || !activeContainer) return;

    // If we drag an item over a new container
    if (activeContainer !== overContainer) {
      setItems(prev => {
        const activeItems = prev[activeContainer];
        const overItems = prev[overContainer];

        const overIndex = overItems.findIndex(item => item.id === over.id);
        const activeIndex = activeItems.findIndex(item => item.id === active.id);

        let newIndex;

        if (overId in items) {
          newIndex = overItems.length + 1;
        } else {
          const isBelowOverItem =
            over &&
            active.rect.current.translated &&
            active.rect.current.translated.top > over.rect.top + over.rect.height;

          const modifier = isBelowOverItem ? 1 : 0;
          newIndex = overIndex >= 0 ? overIndex + modifier : overItems.length + 1;
        }

        return {
          ...items,
          [activeContainer]: items[activeContainer].filter(item => item.id !== active.id),
          [overContainer]: [
            ...items[overContainer].slice(0, newIndex),
            items[activeContainer][activeIndex],
            ...items[overContainer].slice(newIndex, items[overContainer].length),
          ],
        };
      });
    }

    // If we drag and drop in the same container
    setItems(prev => {
      const activeItems = prev[activeContainer];
      const oldIndex = activeItems.findIndex(item => item.id === active.id);
      const newIndex = activeItems.findIndex(item => item.id === over.id);

      return {
        ...prev,
        [activeContainer]: arrayMove(activeItems, oldIndex, newIndex),
      };
    });
  }

  const [isCollapsed, setIsCollapsed] = useState(false);

  // Initialize custom fields collapsed state from sessionStorage (persists across navigation)
  const [isCustomFieldsCollapsed, setIsCustomFieldsCollapsed] = useState(() => {
    if (!global.tableId) return true;
    try {
      const stored = sessionStorage.getItem(`${global.tableId}-columnSelectorCustomFieldsExpanded`);
      // stored === 'true' means expanded, so collapsed = false
      return stored !== 'true';
    } catch {
      return true;
    }
  });

  // Persist custom fields collapsed state to sessionStorage when it changes
  const toggleCustomFieldsCollapsed = () => {
    const newCollapsed = !isCustomFieldsCollapsed;
    setIsCustomFieldsCollapsed(newCollapsed);
    if (global.tableId) {
      try {
        // Store as 'true' when expanded (collapsed = false)
        sessionStorage.setItem(
          `${global.tableId}-columnSelectorCustomFieldsExpanded`,
          newCollapsed ? 'false' : 'true'
        );
      } catch {
        // Ignore sessionStorage errors
      }
    }
  };

  const defaultColumnsItems = items?.defaultColumns?.filter(
    item =>
      item.columnDef.header
        ?.toString()
        ?.toLocaleLowerCase()
        ?.includes(search?.toLocaleLowerCase())
  );

  const frozenColumnsItems = items?.frozenColumns?.filter(
    item =>
      item.columnDef.header
        ?.toString()
        ?.toLocaleLowerCase()
        ?.includes(search?.toLocaleLowerCase())
  );

  const customFieldsItems = items?.customFields?.filter(
    item =>
      item.columnDef.header
        ?.toString()
        ?.toLocaleLowerCase()
        ?.includes(search?.toLocaleLowerCase())
  );

  // Check if there are any custom fields to display
  const hasCustomFields = (items?.customFields?.length || 0) > 0;

  const isColumnSelectorOpen = Boolean(anchorEl);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    const isEnter = event.key === 'Enter';
    const isEscape = event.key === 'Escape';
    if ((isEnter || isEscape) && isColumnSelectorOpen) {
      handleClose();
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearch('');
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    global.event?.('columnSelectorClicked', null);
  };

  const id = isColumnSelectorOpen ? 'table-column-selector-popover' : undefined;
  return (
    <>
      <Button
        sx={{
          ...(isColumnSelectorOpen
            ? {
                background: theme.palette.bg.brandPrimary,
                color: theme.palette.text.brandSecondary,
              }
            : {}),
        }}
        startIcon={
          <Layout
            data-testid={`table_${global.tableId}_column_selector_icon`}
            color="currentColor"
            fill="currentColor"
            size={20}
          />
        }
        variant="tertiary"
        size="lg"
        onClick={handleClick}
        aria-describedby={id}
        data-testid={`table_${global.tableId}_column_selector`}
      >
        <Trans>Column selector</Trans>
      </Button>
      <StyledPopover
        id={id}
        open={isColumnSelectorOpen}
        anchorEl={anchorEl}
        onClose={handleClose}
        onKeyDown={handleKeyPress}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableRestoreFocus
        isCollapsed={isCollapsed}
        key={isCollapsed ? undefined : `popover-expanded`}
        data-testid={`table_${global.tableId}_column_selector_popover`}
      >
        <Header data-testid={`table_${global.tableId}_column_selector_popover_header`}>
          <TextInput
            value={search}
            fullWidth
            placeholder={t`Search...`}
            variant="outlined"
            size="small"
            onChange={e => setSearch(prev => e.target.value)}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <MagnifyingGlass size={16} />
                </InputAdornment>
              ),
            }}
            data-testid={`table_${global.tableId}_column_selector_popover_search`}
          />
        </Header>
        <ListWrapper>
          {/* General columns section */}
          <ListHeader data-testid={`table_${global.tableId}_column_selector_popover_list_header`}>
            <IconButton onClick={() => setIsCollapsed(!isCollapsed)}>
              {isCollapsed ? (
                <CaretRight
                  data-testid={`table_${global.tableId}_column_selector_popover_list_header_caret_down`}
                  size={16}
                />
              ) : (
                <CaretDown
                  data-testid={`table_${global.tableId}_column_selector_popover_list_header_caret_up`}
                  size={16}
                />
              )}
            </IconButton>
            <CheckboxWrapper hasIcon={false}>
              <Switch
                size="small"
                checked={areAllGeneralColumnsVisible}
                onChange={() => toggleGeneralColumnsVisibility()}
                data-testid={`table_${global.tableId}_column_selector_popover_list_header_general_switch`}
              />
              <Text
                variant="labelSm"
                weight="semiBold"
                data-testid={`table_${global.tableId}_column_selector_popover_list_header_general_title`}
              >
                <Trans>General</Trans>
              </Text>
            </CheckboxWrapper>
          </ListHeader>
          <ListContainer
            data-testid={`table_${global.tableId}_column_selector_popover_list_header_general_item_container`}
          >
            <Collapse in={!isCollapsed}>
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
              >
                {frozenColumnsItems.length > 0 && (
                  <FrozenColumnsContainer>
                    <SortableContainer
                      id="frozenColumns"
                      items={frozenColumnsItems}
                      strategy={verticalListSortingStrategy}
                      tableId={global.tableId}
                    />
                  </FrozenColumnsContainer>
                )}
              </DndContext>
              <DndContext
                sensors={sensors}
                collisionDetection={rectIntersection}
                onDragEnd={handleDragEnd}
                onDragOver={handleDragOver}
              >
                <SortableContainer
                  id="defaultColumns"
                  items={defaultColumnsItems}
                  strategy={verticalListSortingStrategy}
                  tableId={global.tableId}
                />
              </DndContext>
            </Collapse>
          </ListContainer>

          {/* Custom fields section - only shown if there are custom fields */}
          {hasCustomFields && (
            <>
              <ListHeader
                data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields`}
              >
                <IconButton onClick={toggleCustomFieldsCollapsed}>
                  {isCustomFieldsCollapsed ? (
                    <CaretRight
                      data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields_caret_down`}
                      size={16}
                    />
                  ) : (
                    <CaretDown
                      data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields_caret_up`}
                      size={16}
                    />
                  )}
                </IconButton>
                <CheckboxWrapper hasIcon={false}>
                  <Switch
                    size="small"
                    checked={areAllCustomFieldsVisible}
                    onChange={() => toggleCustomFieldsVisibility()}
                    onClick={e => e.stopPropagation()}
                    data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields_switch`}
                  />
                  <Text
                    variant="labelSm"
                    weight="semiBold"
                    data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields_title`}
                  >
                    <Trans>Custom Fields</Trans>
                  </Text>
                </CheckboxWrapper>
              </ListHeader>
              <ListContainer
                data-testid={`table_${global.tableId}_column_selector_popover_list_header_custom_fields_item_container`}
              >
                <Collapse in={!isCustomFieldsCollapsed}>
                  <DndContext
                    sensors={sensors}
                    collisionDetection={rectIntersection}
                    onDragEnd={handleDragEnd}
                    onDragOver={handleDragOver}
                  >
                    <SortableContainer
                      id="customFields"
                      items={customFieldsItems}
                      strategy={verticalListSortingStrategy}
                      tableId={global.tableId}
                    />
                  </DndContext>
                </Collapse>
              </ListContainer>
            </>
          )}
        </ListWrapper>
      </StyledPopover>
    </>
  );
};

export const Container = styled.div`
  position: relative;
`;
export const FrozenColumnsContainer = styled.div`
  margin-bottom: 5px;
`;

export const Divider = styled.div`
  width: 100%;
  min-height: 1px;
  background: #cccccc;
`;

const Header = styled.div`
  padding: 0px 16px;
  p {
    line-height: 140%;
    margin: 16px 0px 0px 16px;
  }
  margin-bottom: 4px;
`;

const ListWrapper = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
  max-height: 548px;
  overflow: auto;

  ::-webkit-scrollbar {
    width: 16px;
  }

  ::-webkit-scrollbar-track {
    border-radius: 7px;
    border: 1px solid #transparent;
  }

  ::-webkit-scrollbar-thumb {
    border: 3px solid transparent;
    background-clip: content-box;
    border-radius: 7px;
    opacity: 0.5;
    background-color: ${p => p.theme.palette.bg.disabled};
  }
`;

const ListContainer = styled.div`
  margin-left: 0;
  padding: 0px 16px;
`;

const ListHeader = styled.div`
  display: flex;
  gap: 4px;
  width: 100%;
  user-select: none;
  transition: fill 200ms cubic-bezier(0.4, 0, 0.2, 1) 0ms;
  align-items: center;
  cursor: pointer;
  padding: 0px 16px;
  p {
    line-height: 140%;
    margin: 0px 0px 0px 16px;
    font-weight: 700;
  }
  .checkboxWrapper {
    height: 20px;
    cursor: default;
    .hiddenDotsSixVertical {
      display: none;
    }
  }
`;

const StyledPopover = styled(Popover)<{ isCollapsed: boolean }>`
  .MuiPopover-paper {
    display: flex;
    gap: 16px;
    flex-direction: column;
    background-color: ${p => p.theme?.palette?.bg?.primary};
    max-height: calc(100vh - 80px);
    ${p => !p.isCollapsed && 'min-height: 200px;'}
    width: 320px;
    border-radius: 8px;
    border: 0.5px solid ${p => p.theme?.palette?.border?.primarySubtle};
    overflow: hidden;
    padding: 16px 0px;
    margin-top: 5px;
    box-shadow:
      0px 12px 16px -4px rgba(16, 24, 40, 0.08),
      0px 4px 6px -2px rgba(16, 24, 40, 0.03);
    .MuiInputBase-root {
      margin: 0;
    }
  }
`;
